import { baseUrlEndpoint, baseUrlPattern, resolveBaseUrl } from "../baseUrl";
import {
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const DEFAULT_OPENROUTER_BASE_URL = "https://openrouter.ai/api/v1";

const openRouterModelsFetch = openAiModelsFetch(
  "Fetch models from OpenRouter",
  ({ baseUrl }) =>
    baseUrlEndpoint(baseUrl, DEFAULT_OPENROUTER_BASE_URL, "/models")
);

export const openRouter = defineConnector({
  identifier: "openRouter",
  name: "OpenRouter",
  tagLine: "Unified LLM gateway",
  keywords: "llm gateway proxy models inference claude gpt gemini llama",
  logo,
  protocol: "http",
  tags: ["ai", "engineering", "data"],
  defaultUrlPattern: ({ baseUrl }) =>
    baseUrlPattern(baseUrl, DEFAULT_OPENROUTER_BASE_URL),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Create an API key",
      items: [
        {
          kind: "markdown",
          content:
            "Open [OpenRouter API keys](https://openrouter.ai/docs/api-keys) and create an API key. OpenRouter authenticates requests with `Authorization: Bearer <OPENROUTER_API_KEY>`.",
        },
      ],
    },
    {
      title: "Enter your OpenRouter API key",
      items: [
        {
          kind: "input",
          label: "API key",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
        {
          kind: "url",
          label: "Base URL (optional)",
          placeholder: DEFAULT_OPENROUTER_BASE_URL,
          defaultValue: DEFAULT_OPENROUTER_BASE_URL,
          target: { kind: "configuration", field: "baseUrl" },
          helperText: "Leave blank for the default OpenRouter API endpoint.",
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [
            { field: "name", placeholder: "anthropic/claude-opus-4-8" },
          ],
          addLabel: "Add model",
          fetch: openRouterModelsFetch,
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ baseUrl }) =>
      baseUrlEndpoint(baseUrl, DEFAULT_OPENROUTER_BASE_URL, "/key"),
  },
  publicEnvVars: ({ baseUrl }) => ({
    OPEN_ROUTER_BASE_URL: resolveBaseUrl(baseUrl, DEFAULT_OPENROUTER_BASE_URL),
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.OPEN_ROUTER_BASE_URL as the OpenRouter API base URL. Pass a placeholder API key; the proxy injects the real key from the connected app.",
      configuration
    ),
  usage: openaiUsage(),
});
